import { useState, useMemo, useEffect } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PanelContent from '@shared/components/PanelContent';
import CollapsibleFilterBar from '@shared/components/CollapsibleFilterBar';
import CoverageFilter from '@shared/components/CoverageFilter';
import InlineEditField from '@shared/components/InlineEditField';
import { productTypeChipSx } from '@shared/constants/product';
import { useConnectivityStore } from '@shared/hooks/useConnectivityStore';
import { useMapStore } from '@shared/hooks/useMapStore';
import { PeriodSelector, usePeriodStore, currencySymbol } from '@shared/pricing';
import { useBasket } from '../hooks/useBasket';
import { useCreateOrder } from '@features/orders/hooks/useCreateOrder';
import BasketItemList from './BasketItemList';

const PRODUCT_TYPES = ['All', 'AVCS', 'ADP', 'e-NP'] as const;

export default function BasketPanel() {
  const items = useBasket((s) => s.items);
  const clearBasket = useBasket((s) => s.clearBasket);
  const batchChangeAvcs = useBasket((s) => s.batchChangeAvcs);
  const orderName = useBasket((s) => s.orderName);
  const setOrderName = useBasket((s) => s.setOrderName);
  const note = useBasket((s) => s.note);
  const setNote = useBasket((s) => s.setNote);
  const isOffline = useConnectivityStore((s) => s.state) !== 'online';
  const createOrder = useCreateOrder();
  const [showSuccess, setShowSuccess] = useState(false);
  const requestedPeriod = usePeriodStore((s) => s.requestedPeriodMonths);
  const setRequestedPeriod = usePeriodStore((s) => s.setRequestedPeriodMonths);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [coverageFilter, setCoverageFilter] = useState<string[]>([]);

  // Map integration: activate basket products on mount, deactivate on unmount
  const setBasketProductsActive = useMapStore((s) => s.setBasketProductsActive);
  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);
  const setCatalogProducts = useMapStore((s) => s.setCatalogProducts);

  useEffect(() => {
    setBasketProductsActive(true);
    setActiveProductsRouteId(null);
    setCatalogProducts(null);
    return () => {
      setBasketProductsActive(false);
    };
  }, [setBasketProductsActive, setActiveProductsRouteId, setCatalogProducts]);

  const hasAvcsItems = useMemo(() => items.some((i) => i.productType === 'AVCS'), [items]);

  // Totals grouped by currency (display-only)
  const totals = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      if (item.priceAmount != null && item.currencyCode) {
        map.set(item.currencyCode, (map.get(item.currencyCode) ?? 0) + item.priceAmount);
      }
    }
    return Array.from(map, ([code, amount]) => ({ code, amount }));
  }, [items]);

  function handleBatchPeriodChange(months: number) {
    setRequestedPeriod(months);
    batchChangeAvcs(months);
  }

  function handleCreateOrder() {
    setShowSuccess(false);
    createOrder.mutate(
      {
        name: orderName || `Order — ${items.length} items`,
        comment: note || undefined,
        items: items.map((i) => ({
          productId: i.productId,
          productType: i.productType,
          name: i.name,
          licensePeriodMonths: i.licensePeriodMonths,
        })),
      },
      {
        onSuccess: () => {
          clearBasket();
          setShowSuccess(true);
        },
      },
    );
  }

  return (
    <PanelContent>
      <div data-testid="basket-panel" className="flex flex-col gap-2">
        {/* Top order button (full width, with totals inside) */}
        {items.length > 0 && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleCreateOrder}
            disabled={isOffline || createOrder.isPending}
            startIcon={createOrder.isPending ? <CircularProgress size={16} /> : undefined}
            size="small"
          >
            {createOrder.isPending
              ? 'Creating...'
              : totals.length > 0
                ? `Create Order — ${totals.map((t) => `${currencySymbol(t.code)}${t.amount.toFixed(2)}`).join(' / ')}`
                : 'Create Order'}
          </Button>
        )}

        {/* Order name + note */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <InlineEditField
            value={orderName}
            onChange={setOrderName}
            placeholder="Write order name..."
            variant="subtitle2"
            fontWeight={600}
          />
          <InlineEditField
            value={note}
            onChange={setNote}
            placeholder="Write a note..."
            variant="caption"
            multiline
            rows={2}
          />
        </Box>

        {/* Search + AVCS Period selector on the same row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search basket..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {hasAvcsItems && (
            <PeriodSelector
              value={requestedPeriod}
              onChange={handleBatchPeriodChange}
              label="AVCS Period"
              size="small"
            />
          )}
        </Box>

        <CollapsibleFilterBar
          activeFilterCount={(typeFilter !== 'All' ? 1 : 0) + (coverageFilter.length > 0 ? 1 : 0)}
          onClear={() => {
            setTypeFilter('All');
            setCoverageFilter([]);
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {PRODUCT_TYPES.map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                variant={typeFilter === type ? 'filled' : 'outlined'}
                color={type === 'All' ? (typeFilter === type ? 'primary' : 'default') : undefined}
                sx={productTypeChipSx(type === 'All' ? '' : type, typeFilter === type)}
                onClick={() => setTypeFilter(type)}
              />
            ))}
          </Box>
          <Box sx={{ minWidth: 140 }}>
            <CoverageFilter value={coverageFilter} onChange={setCoverageFilter} size="small" />
          </Box>
        </CollapsibleFilterBar>

        {/* Header row: item count + Clear all */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Basket ({items.length} {items.length === 1 ? 'item' : 'items'})
          </Typography>
          {items.length > 0 && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={clearBasket}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
          )}
        </Box>

        <BasketItemList search={search} typeFilter={typeFilter} coverageFilter={coverageFilter} />

        {items.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCreateOrder}
              disabled={isOffline || createOrder.isPending}
              startIcon={createOrder.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {createOrder.isPending
                ? 'Creating Order...'
                : totals.length > 0
                  ? `Create Order — ${totals.map((t) => `${currencySymbol(t.code)}${t.amount.toFixed(2)}`).join(' / ')}`
                  : 'Create Order'}
            </Button>
          </Box>
        )}

        {createOrder.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {createOrder.error?.message ?? 'Failed to create order'}
          </Alert>
        )}

        {showSuccess && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Order created successfully. Basket has been cleared.
          </Alert>
        )}
      </div>
    </PanelContent>
  );
}
